import React, { useImperativeHandle, forwardRef, useCallback } from 'react';
import CommentsList from '../CommentsList/CommentsList';
import { useComments } from '../../../../hooks/useComments';

/**
 * SubFormComments - A wrapper component that displays comments for a specific subform
 * Uses server-side pagination and filtering
 */
const SubFormCommentsSection = forwardRef(
  (
    { persona, requestId, subFormId, onCommentsRefresh, requestProgressId },
    ref
  ) => {
    // Use the useComments hook with subFormId filter for server-side pagination
    const {
      prevComments: subFormComments,
      loadingComments,
      pagination,
      goToPage,
      nextPage,
      prevPage,
      sortBy,
      sortOrder,
      updateSorting,
      filters,
      updateFilters,
      setPageLimit,
      fetchComments,
      handleEditComment,
      handleDeleteComment,
    } = useComments({
      persona,
      requestId,
      initialFilters: { subFormId }, // Filter by subFormId on the server
      skipInitialFetch: false,
    });

    // Expose methods to parent component
    useImperativeHandle(
      ref,
      () => ({
        refreshComments: () => {
          // Refresh comments for this subform
          if (fetchComments) {
            fetchComments(1, { filters: { ...filters, subFormId } });
          }

          if (onCommentsRefresh) {
            onCommentsRefresh();
          }
        },
        isAddingComment: () => false, // Will be managed by parent
      }),
      [fetchComments, onCommentsRefresh, filters, subFormId]
    );

    // Stable handlers to avoid re-renders
    const handleUpdateSorting = useCallback(
      (field, order) => {
        updateSorting(field, order);
      },
      [updateSorting]
    );

    const handleUpdateFilters = useCallback(
      (newFilters) => {
        // Always maintain the subFormId filter
        updateFilters({ ...newFilters, subFormId });
      },
      [updateFilters, subFormId]
    );

    const handleSetPageLimit = useCallback(
      (limit) => {
        setPageLimit(limit);
      },
      [setPageLimit]
    );

    return (
      <CommentsList
        prevComments={subFormComments}
        pagination={pagination}
        goToPage={goToPage}
        nextPage={nextPage}
        prevPage={prevPage}
        sortBy={sortBy}
        sortOrder={sortOrder}
        updateSorting={handleUpdateSorting}
        filters={filters}
        updateFilters={handleUpdateFilters}
        loadingComments={loadingComments}
        setPageLimit={handleSetPageLimit}
        showControls={true}
        showFilters={false}
        showSorting={false}
        showPageSize={false}
        showResultsInfo={false}
        showPagination={true}
        persona={persona}
        onEdit={handleEditComment}
        onDelete={handleDeleteComment}
      />
    );
  }
);

export default SubFormCommentsSection;
